/**
 * CreditsContext — Nexus v29
 *
 * Single source of truth for the user's credit balance across the whole app.
 * - Fetches on mount + poll every 15 s.
 * - Exposes `refresh()` for immediate refresh after events (e.g. successful
 *   payment webhook, chat message that consumed credits, etc.).
 * - Exposes `openCreditsPanel(returnTo?)` — every "credits" button in the
 *   app calls this. It renders the SAME <CreditsPanel /> from any context.
 *
 * Real-time is polling-based (no WebSocket infra yet). Poll interval is
 * short (15 s) but throttled to avoid spamming while the tab is hidden.
 */
import React, {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

type Balance = {
  credits_balance: number;
  equivalent_usd: number;
  status: 'free' | 'paid';
  welcome_offer_available: boolean;
};

type WelcomeOffer = Awaited<ReturnType<typeof apiService.getWelcomeOffer>>;

type CreditsContextValue = {
  balance: number | null;
  status: 'free' | 'paid';
  loading: boolean;
  welcomeOffer: WelcomeOffer | null;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  setBalanceOptimistic: (value: number) => void;
  isPanelOpen: boolean;
  openCreditsPanel: (returnTo?: string) => void;
  closeCreditsPanel: () => void;
  panelReturnTo: string | null;
  // Sprint Estabilización Final · Feb 2026 · fast-poll while a build
  // is running so the -7 overdraft pause reaches the UI in <2s
  // instead of waiting for the next 15s tick. Callers pass a
  // seconds-based TTL — polling reverts to normal automatically.
  activateFastPoll: (ttlSeconds?: number) => void;
};

const CreditsContext = createContext<CreditsContextValue | null>(null);

const POLL_INTERVAL_MS = 15_000;
const FAST_POLL_INTERVAL_MS = 2_000;

export const CreditsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [status, setStatus] = useState<'free' | 'paid'>('free');
  const [loading, setLoading] = useState(false);
  const [welcomeOffer, setWelcomeOffer] = useState<WelcomeOffer | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelReturnTo, setPanelReturnTo] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  // Sprint Estabilización Final · Feb 2026 · fast-poll state
  const [fastPollUntil, setFastPollUntil] = useState<number>(0);
  const activateFastPoll = useCallback((ttlSeconds: number = 180) => {
    // Cap TTL to 5 minutes so a stale flag never keeps hammering.
    const capped = Math.min(Math.max(ttlSeconds, 5), 300);
    setFastPollUntil(Date.now() + capped * 1000);
  }, []);

  const isAuthed = () =>
    typeof window !== 'undefined' && !!localStorage.getItem('nexus_token');

  const refresh = useCallback(async (opts?: { force?: boolean }) => {
    if (!isAuthed()) {
      setBalance(null);
      setWelcomeOffer(null);
      return;
    }
    // de-duplicate concurrent refreshes UNLESS the caller requests force.
    if (inFlightRef.current && !opts?.force) return inFlightRef.current;
    const task = (async () => {
      try {
        setLoading(true);
        const [b, wo] = await Promise.all([
          apiService.getBillingBalance(),
          apiService.getWelcomeOffer().catch(() => null),
        ]);
        // Sprint P0 Quirúrgico Feb 2026 · mostrar sobregiro real
        // (negativos permitidos hasta MAX_OVERDRAFT=-7). Antes
        // clampábamos a 0 y el usuario no veía cuánto debía.
        setBalance(Math.floor(b.credits_balance));
        setStatus(b.status);
        setWelcomeOffer(wo);
      } catch {
        // silent — never block UI on billing failure
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = task;
    return task;
  }, []);

  /**
   * Optimistic setter — used after Admin Pass / self-recharge which
   * returns the authoritative balance_after. Avoids the race with a
   * concurrent scheduled refresh that might overwrite with stale data.
   */
  const setBalanceOptimistic = useCallback((value: number) => {
    const v = Math.floor(value);
    setBalance(v);
    // Also schedule a forced refresh so any subsequent poll doesn't
    // clobber (though our force flag protects against the de-dup path).
    setTimeout(() => { void refresh({ force: true }); }, 300);
  }, [refresh]);

  // Refresh whenever the auth state flips (login/register/logout). Without
  // this, the CreditsProvider mounts BEFORE the user's token is set and the
  // first `refresh()` returns early. The chip would then display an em-dash
  // until the next 15 s tick — bad first impression on registration.
  useEffect(() => {
    if (isAuthenticated) {
      void refresh({ force: true });
    } else {
      setBalance(null);
      setWelcomeOffer(null);
    }
  }, [isAuthenticated, refresh]);

  // Poll while tab is visible.
  useEffect(() => {
    void refresh();
    const tick = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        void refresh();
      }
    };
    // Sprint Estabilización Final · Feb 2026 · pick fast interval when
    // a caller (WorkspaceUser) told us a build is in flight. Interval
    // auto-reverts to 15s once `fastPollUntil` expires.
    const active = fastPollUntil > Date.now();
    const interval = active ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    pollingRef.current = window.setInterval(tick, interval) as unknown as number;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (pollingRef.current !== null) window.clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, fastPollUntil]);

  const openCreditsPanel = useCallback((returnTo?: string) => {
    setPanelReturnTo(returnTo ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : null));
    setIsPanelOpen(true);
  }, []);

  const closeCreditsPanel = useCallback(() => {
    setIsPanelOpen(false);
    // Give the modal exit animation a moment before clearing return_to.
    window.setTimeout(() => setPanelReturnTo(null), 300);
  }, []);

  const value = useMemo<CreditsContextValue>(() => ({
    balance,
    status,
    loading,
    welcomeOffer,
    refresh,
    setBalanceOptimistic,
    isPanelOpen,
    openCreditsPanel,
    closeCreditsPanel,
    panelReturnTo,
    activateFastPoll,
  }), [balance, status, loading, welcomeOffer, refresh, setBalanceOptimistic,
       isPanelOpen, openCreditsPanel, closeCreditsPanel, panelReturnTo,
       activateFastPoll]);

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};

export function useCredits(): CreditsContextValue {
  const ctx = useContext(CreditsContext);
  if (!ctx) {
    // Non-fatal — fall back to a stub so components don't crash when
    // rendered outside the provider (e.g., isolated Storybook).
    return {
      balance: null,
      status: 'free',
      loading: false,
      welcomeOffer: null,
      refresh: async () => {},
      setBalanceOptimistic: () => {},
      isPanelOpen: false,
      openCreditsPanel: () => {},
      closeCreditsPanel: () => {},
      panelReturnTo: null,
      activateFastPoll: () => {},
    };
  }
  return ctx;
}
